import { useCallback, useRef, useState } from 'react';
import { HISTORY_LIMIT } from './imageEditorTypes';

interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
}

export function useEditorHistory<T>(initialState: T) {
  const [history, setHistory] = useState<HistoryState<T>>({
    past: [],
    present: initialState,
    future: [],
  });

  // Track whether we should skip pushing to history (during drag)
  const skipRef = useRef(false);
  // Snapshot of state before a drag begins
  const preDragRef = useRef<T | null>(null);

  const push = useCallback((newState: T) => {
    if (skipRef.current) {
      // During drag: update present without recording history
      setHistory((h) => ({ ...h, present: newState }));
      return;
    }
    setHistory((h) => {
      const past = [...h.past, h.present].slice(-HISTORY_LIMIT);
      return { past, present: newState, future: [] };
    });
  }, []);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.past.length === 0) return h;
      const prev = h.past[h.past.length - 1];
      const past = h.past.slice(0, -1);
      return { past, present: prev, future: [h.present, ...h.future] };
    });
  }, []);

  const redo = useCallback(() => {
    setHistory((h) => {
      if (h.future.length === 0) return h;
      const next = h.future[0];
      const future = h.future.slice(1);
      return { past: [...h.past, h.present], present: next, future };
    });
  }, []);

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  // Call before drag starts — snapshots current state and silences history recording
  const startDrag = useCallback(() => {
    setHistory((h) => {
      preDragRef.current = h.present;
      return h;
    });
    skipRef.current = true;
  }, []);

  // Call on drag end — records the pre-drag snapshot in past, keeps current present
  const endDrag = useCallback(() => {
    skipRef.current = false;
    const snapshot = preDragRef.current;
    preDragRef.current = null;
    if (snapshot === null) return;
    setHistory((h) => {
      // If nothing actually changed during drag, skip
      if (h.present === snapshot) return h;
      const past = [...h.past, snapshot].slice(-HISTORY_LIMIT);
      return { past, present: h.present, future: [] };
    });
  }, []);

  const reset = useCallback((state: T) => {
    setHistory({ past: [], present: state, future: [] });
  }, []);

  return {
    state: history.present,
    push,
    undo,
    redo,
    canUndo,
    canRedo,
    startDrag,
    endDrag,
    reset,
  };
}
